/**
 * Byte-synced with the oportal copy — see ../_SYNC.md.
 * Import only `vue` and the renderer's own modules — never vue-i18n, httpClient or OPAL.
 */
import { computed, inject, provide, reactive, ref, watch, } from "vue";
import { formatMinor as formatMinorRaw } from "../composables/useCurrency";
import { localized as localizedRaw } from "../composables/localized";
import { resolveActivePresets } from "../composables/presets";
import { sanitizeDigits } from "../composables/format";
export const DonationKey = Symbol("donation");
export function createDonationContext(config, options = {}) {
    const firstStepId = config.steps[0]?.id ?? "amount";
    const seedLang = options.initialLocale && config.supportedLocales.includes(options.initialLocale)
        ? options.initialLocale
        : config.defaultLocale;
    const defaultPresetAmount = (freq) => {
        const list = config.presetsByFrequency[freq];
        return (list.find((p) => p.isDefault) ?? list[0])?.amountMinor ?? 0;
    };
    const state = reactive({
        lang: seedLang,
        stepId: firstStepId,
        freq: config.defaultFrequency,
        amountMinor: defaultPresetAmount(config.defaultFrequency),
        custom: "",
        donorType: "Individual",
        civility: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        birthDate: "",
        addressLine1: "",
        addressLine2: "",
        postalCode: "",
        city: "",
        country: "",
        orgName: "",
        siret: "",
        legalForm: "",
        projectId: null,
        gdprConsentGranted: false,
        newsletterConsentGranted: false,
    });
    // eslint-disable-next-line prefer-const
    let context;
    /* ----- author-content i18n + locale ----- */
    const lang = computed(() => state.lang);
    const availableLangs = computed(() => config.supportedLocales);
    const setLang = (code) => {
        if (config.supportedLocales.includes(code)) {
            state.lang = code;
        }
    };
    const localized = (text) => localizedRaw(text, state.lang, config.defaultLocale);
    /* ----- theme ----- */
    const themeVars = computed(() => config.themeVars);
    const bannerImageUrl = computed(() => config.bannerImageUrl);
    const bannerImagePosition = computed(() => config.bannerImagePosition);
    // While false, the amount follows the default preset as `config` changes under a live instance.
    let hasUserSelectedAmount = false;
    /* ----- frequency ----- */
    const frequency = computed(() => state.freq);
    const setFrequency = (f) => {
        if (config.frequencies.some((x) => x.frequency === f)) {
            // Reset BEFORE state.freq: the flush:'sync' activePresets watcher would read the stale flag.
            hasUserSelectedAmount = false;
            state.freq = f;
        }
    };
    const frequencyOptions = computed(() => config.frequencies.map((f) => ({ value: f.frequency })));
    const isRecurring = computed(() => state.freq !== "OneOff");
    const frequencySuffixToken = computed(() => state.freq === "Monthly" ? "perMonth" : state.freq === "Annual" ? "perYear" : "");
    const activeFrequency = computed(() => config.frequencies.find((f) => f.frequency === state.freq));
    /* ----- amount ----- */
    const allowCustom = computed(() => activeFrequency.value?.allowCustomAmount ?? false);
    const amountMinor = computed(() => {
        if (state.custom !== "" && allowCustom.value) {
            const major = parseInt(state.custom, 10);
            return Number.isNaN(major) ? 0 : major * 100;
        }
        return state.amountMinor;
    });
    const minimumAmountMinor = computed(() => {
        const min = config.document.settings.minimumAmountMinor;
        return typeof min === "number" ? min : null;
    });
    const activePresets = computed(() => resolveActivePresets(config.presetsByFrequency, state.freq));
    const presets = computed(() => activePresets.value.map((p) => ({
        id: p.id,
        amountMinor: p.amountMinor,
        label: localized(p.label),
        description: localized(p.description),
        imageUrl: p.imageUrl,
        selected: state.custom === "" && state.amountMinor === p.amountMinor,
        select: () => setAmountMinor(p.amountMinor),
    })));
    const setAmountMinor = (n) => {
        state.amountMinor = n;
        state.custom = "";
        hasUserSelectedAmount = true;
    };
    const setCustom = (raw) => {
        state.custom = sanitizeDigits(raw);
        state.amountMinor = 0;
        hasUserSelectedAmount = true;
    };
    const custom = computed(() => state.custom);
    // flush: "sync": the re-seed must land synchronously — callers assert right after setFrequency/config edits.
    watch(activePresets, (list) => {
        if (state.custom !== "")
            return;
        const stillPresent = list.some((p) => p.amountMinor === state.amountMinor);
        if (hasUserSelectedAmount && stillPresent)
            return;
        state.amountMinor = (list.find((p) => p.isDefault) ?? list[0])?.amountMinor ?? 0;
    }, { immediate: true, flush: "sync" });
    const currency = computed(() => config.defaultCurrency);
    const formatMinor = (n) => formatMinorRaw(n, config.defaultCurrency, state.lang);
    /* ----- donor / projects ----- */
    const projectOptions = computed(() => config.projects.map((p) => ({ projectId: p.projectId, label: localized(p.label) })));
    const showProjectSelector = computed(() => config.projects.length > 1 ||
        (config.projects.length >= 1 &&
            config.document.settings.showAffectationWhenSingleProject));
    const requirePhone = computed(() => config.document.settings.requirePhone);
    // `!== false` / `=== true`, never truthiness: a wire document is not type-checked at runtime, and
    // an absent key must resolve to the flag's documented default, as the backend decoder does.
    const showNewsletterOptIn = computed(() => config.document.settings.showNewsletterOptIn !== false);
    const requireConsent = computed(() => config.document.settings.requireConsent !== false);
    const enableBirthDate = computed(() => config.document.settings.enableBirthDate === true);
    const allowCompanyDonation = computed(() => config.document.settings.allowCompanyDonation === true);
    // Fires only when the document changes under a mounted instance (the oportal live preview).
    watch(allowCompanyDonation, (allowed) => {
        if (!allowed) {
            state.donorType = "Individual";
        }
    }, { flush: "sync" });
    const consentLabel = computed(() => localized(config.document.settings.consentText));
    const legalNotice = computed(() => localized(config.document.settings.legalNoticeText));
    const isOrganization = computed(() => state.donorType === "Organization");
    const setField = (key, value) => {
        state[key] = value;
    };
    /* ----- steps & navigation ----- */
    const steps = computed(() => config.steps);
    const stepIndex = computed(() => steps.value.findIndex((s) => s.id === state.stepId));
    const currentStep = computed(() => steps.value[stepIndex.value] ?? steps.value[0]);
    const currentStepId = computed(() => state.stepId);
    const progressSteps = computed(() => {
        const visible = steps.value.filter((s) => s.showInProgress !== false && !s.isTerminal);
        const currentOverall = stepIndex.value;
        return visible.map((s, i) => {
            const overall = steps.value.findIndex((x) => x.id === s.id);
            const done = currentOverall > overall;
            const current = state.stepId === s.id;
            return {
                id: s.id,
                index: i,
                number: i + 1,
                label: s.label ? s.label(context) : s.id,
                done,
                current,
                upcoming: !done && !current,
                hasConnector: i < visible.length - 1,
            };
        });
    });
    const canProceed = computed(() => {
        const step = currentStep.value;
        return step.validate ? step.validate(state, context) : true;
    });
    const isFirstStep = computed(() => stepIndex.value <= 0);
    const isTerminalStep = computed(() => !!currentStep.value.isTerminal);
    const goTo = (id) => {
        if (steps.value.some((s) => s.id === id)) {
            state.stepId = id;
        }
    };
    const next = () => {
        if (!canProceed.value) {
            return;
        }
        const nxt = steps.value[stepIndex.value + 1];
        if (nxt && !nxt.isTerminal) {
            state.stepId = nxt.id;
        }
    };
    const back = () => {
        const i = stepIndex.value;
        if (i > 0) {
            state.stepId = steps.value[i - 1].id;
        }
    };
    const reset = () => {
        if (steps.value[0]) {
            state.stepId = steps.value[0].id;
        }
    };
    /* ----- messages ----- */
    const message = (type) => localized(config.messagesByType[type]?.content ?? null);
    /* ----- submission ----- */
    const previewMode = computed(() => config.previewMode);
    const status = ref("idle");
    let handedOver = false;
    // Bound once at context creation and never re-read: a later prop rebinding must not rewrite a
    // visit's attribution. The renderer never reads `window.location`: the host supplies both.
    const utm = options.utm ?? {};
    const attributionToken = options.attributionToken;
    const buildSubmitRequest = (returnUrl) => {
        const req = {
            donorType: state.donorType,
            firstName: state.firstName,
            lastName: state.lastName,
            email: state.email,
            addressLine1: state.addressLine1,
            postalCode: state.postalCode,
            city: state.city,
            country: state.country,
            amountMinor: amountMinor.value,
            currency: config.defaultCurrency,
            frequency: state.freq,
            gdprConsentGranted: state.gdprConsentGranted,
            newsletterConsentGranted: state.newsletterConsentGranted,
        };
        if (state.civility.trim() !== "") {
            req.civility = state.civility;
        }
        if (state.phone.trim() !== "") {
            req.phone = state.phone;
        }
        if (state.birthDate.trim() !== "") {
            req.birthDate = state.birthDate;
        }
        if (state.addressLine2.trim() !== "") {
            req.addressLine2 = state.addressLine2;
        }
        if (state.projectId !== null) {
            req.projectId = state.projectId;
        }
        if (state.donorType === "Organization") {
            req.organization = {
                orgName: state.orgName,
                siret: state.siret,
                ...(state.legalForm.trim() !== "" ? { legalForm: state.legalForm } : {}),
            };
        }
        if (returnUrl !== undefined) {
            req.returnUrl = returnUrl;
        }
        // Per-key `typeof` + blank guard, never a spread: a `null` or a number from an untyped host
        // would throw at `.trim()` and kill the submit, and a blank value must stay off the wire.
        if (typeof utm.utmSource === "string" && utm.utmSource.trim() !== "") {
            req.utmSource = utm.utmSource;
        }
        if (typeof utm.utmMedium === "string" && utm.utmMedium.trim() !== "") {
            req.utmMedium = utm.utmMedium;
        }
        if (typeof utm.utmCampaign === "string" && utm.utmCampaign.trim() !== "") {
            req.utmCampaign = utm.utmCampaign;
        }
        if (typeof attributionToken === "string" && attributionToken.trim() !== "") {
            req.attributionToken = attributionToken;
        }
        return req;
    };
    const submit = async (returnUrl) => {
        if (previewMode.value) {
            return;
        }
        if (!canProceed.value) {
            return;
        }
        status.value = "submitting";
        handedOver = false;
        try {
            // A resolved handler has started the navigation away: stay `submitting` until the page
            // unloads or the host calls resetSubmit().
            await options.onSubmit?.(buildSubmitRequest(returnUrl), context);
            handedOver = true;
        }
        catch (err) {
            status.value = "error";
            options.onError?.(err);
        }
    };
    // CONSTRAINT: never re-enable a submit still in flight; its late completion would race a second
    // click into a second donation.
    const resetSubmit = () => {
        if (handedOver) {
            handedOver = false;
            status.value = "idle";
        }
    };
    const previewOutcomeState = ref("ThankYou");
    const previewOutcome = computed(() => previewOutcomeState.value);
    const simulateOutcome = (type) => {
        if (!previewMode.value) {
            return;
        }
        previewOutcomeState.value = type;
        const terminal = steps.value.find((s) => s.isTerminal);
        if (terminal) {
            state.stepId = terminal.id;
        }
    };
    context = {
        config,
        state,
        lang,
        availableLangs,
        setLang,
        localized,
        themeVars,
        bannerImageUrl,
        bannerImagePosition,
        frequency,
        setFrequency,
        frequencyOptions,
        frequencySuffixToken,
        isRecurring,
        presets,
        amountMinor,
        setAmountMinor,
        custom,
        setCustom,
        allowCustom,
        minimumAmountMinor,
        formatMinor,
        currency,
        projectOptions,
        showProjectSelector,
        requirePhone,
        showNewsletterOptIn,
        requireConsent,
        enableBirthDate,
        allowCompanyDonation,
        consentLabel,
        legalNotice,
        setField,
        isOrganization,
        steps,
        currentStep,
        currentStepId,
        stepIndex,
        progressSteps,
        canProceed,
        isFirstStep,
        isTerminalStep,
        next,
        back,
        goTo,
        reset,
        message,
        previewMode,
        status,
        buildSubmitRequest,
        submit,
        resetSubmit,
        previewOutcome,
        simulateOutcome,
    };
    return context;
}
export function provideDonation(config, options) {
    const ctx = createDonationContext(config, options);
    provide(DonationKey, ctx);
    return ctx;
}
export function useDonation() {
    const ctx = inject(DonationKey);
    if (!ctx) {
        throw new Error("[donation-form] useDonation() must be called inside a <DonationForm>.");
    }
    return ctx;
}
